import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux'
import 'ol/ol.css';
import CursorPosition from './CursorPosition.jsx'
import Crackmeter from './Crackmeter.jsx'
import Scale from './Scale.jsx'
import Map from 'ol/Map'
import {Point, Polygon, LineString} from 'ol/geom';
import TileGrid from 'ol/tilegrid/TileGrid';
import {Icon, Stroke, Style, Text, Fill, Circle, RegularShape} from 'ol/style';
import {unByKey} from 'ol/Observable';
import { shiftKeyOnly } from 'ol/events/condition.js';
import { createEmpty, extend, isEmpty } from 'ol/extent';
import {make_generic_proxy_url} from '../app/utils';
import { runInParallel } from '../app/runInParallel.js';

import Feature from 'ol/Feature'
import View from 'ol/View'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import XYZ from 'ol/source/XYZ'
import { useSelector } from 'react-redux'
import { selectLayers, setLayers, layersApi } from '../app/layers.js'
import { usersApi, setUsers } from '../app/users.js'
import { 
  selectActivePanel, 
  selectAnnotationMethod,
  setTopbar,
  setAnnotationsForGeometryEdit,
  getAnnotationsForGeometryEdit,
  selectMultipleAnnotations,
  selectSelectedAnnotationIds,
  setDisplayedPhoto,
  setNewGeometries,
  getNewGeometries,
  getValidateModifyOnAnnotation,
  setValidateModifyOnAnnotation,
  selectMoveToAnnotationId,
  setMoveToAnnotationId,
  setTranslateOffsetDraft,
  selectTextLabelDraft,
  setTextLabelDraft,
  selectAnnotationSelectTarget,
  setAnnotationSelectTarget,
} from '../app/interface.js'
import { setInspection, inspectionApi } from '../app/inspection.js'
import { 
  selectFilteredAnnotations, 
  selectGetColorRatio, 
  annotationsApi, 
  replaceAnnotations,
  updateViewSpec,
  createAnnotation,
  createAnnotationOnAPI,
  selectAnnotationState,
  updateAnnotationState,
  updateAnnotation,
  selectOptionsWithSymbology,
  getAnnotationSymbology,
  selectAnnotationsStyles,
  setViewAnnotationStyles,
  selectViewStyleChart,
  get_display_value,
  selectAnnotationFields,
  selectViewSpec,
} from '../app/annotations.js'
import { selectCurrentUserId } from '../app/auth.js'
import apiClient from "../app/apiClient.js";
import { useSearchParams } from 'react-router-dom';
import { ActivityTracker } from '../app/activity_tracking.js'
import PhotoPopupDisplayer from './PhotoPopupDisplayer.jsx';
import {
  initMapInteractions,
  reference_to_openlayer_coordinates,
  openlayer_to_reference_coordinates,
  computeViewInformation,
} from '../app/mapInteractions.js';
import { createTextAnnotation, replaceTextAnnotations, selectTextAnnotations, updateTextAnnotation } from '../app/textAnnotations.js';
import SelectedAnnotation from './SelectedAnnotation.jsx';
import TextLabelModal from './TextLabelModal.jsx';
import { Collection } from 'ol';
import ParallelProgressSnackbar from './ParallelProgressSnackbar.jsx';
import TranslateModal from './TranslateModal.jsx';

const GEOMETRY_SAVE_PARALLEL = 20;

const OUTPUTFIELDS = {
  users: `id,name,email,role`
}

const pointClassName = (new Point([0,0])).getType()
const polygonClassName = (new Polygon([[[0,0]]])).getType()
const lineStringClassName = (new LineString([[0,0]])).getType()

const fitMapOptions = (map) => ({
  maxZoom: 18,
  size: map.getSize(),
  padding: [48, 48, 48, 48],
});

function MapWrapper(props) {
  const [searchParams] = useSearchParams();

  const dispatch = useDispatch()
  const viewId = searchParams.get("viewId")
  const inspectionId = searchParams.get("inspectionId")
  const currentUserId = useSelector(selectCurrentUserId)

  const activePanel = useSelector(selectActivePanel)
  const stateLayers = useSelector(selectLayers)
  const annotationMethod = useSelector(selectAnnotationMethod)
  const annotations = useSelector(selectFilteredAnnotations)
  const textAnnotations = useSelector(selectTextAnnotations)
  const getColorRatio = useSelector(selectGetColorRatio)
  const selectedAnnotations = useSelector(selectSelectedAnnotationIds) // List of selected annotation IDs
  // DragBox "remove points" reads these when the box ends
  const annotationsRef = useRef([]);
  const selectedAnnotationsRef = useRef([]);
  annotationsRef.current = annotations;
  selectedAnnotationsRef.current = selectedAnnotations;

  // Convert selected annotations to Set for fast lookup
  const selectedAnnotationsSet = useMemo(() => {
    return new Set(selectedAnnotations);
  }, [selectedAnnotations]);
  const authToken = apiClient.getAuthToken();
  const createAnnotationApi = useSelector(annotationsApi.selectCreate)
  const updateAnnotationApi = useSelector(annotationsApi.update)
  const annotationState = useSelector(selectAnnotationState)
  const annotationsForGeometryEdit = useSelector(getAnnotationsForGeometryEdit)
  // selectOptionsWithSymbology return an object with fields:
  //  - color
  //  - keyName
  //  - keyUid
  //  - id
  //  - level
  //  - symbol
  //  - fill_pattern
  const coloredOptionsFromKeys = useSelector(selectOptionsWithSymbology) // list of options with color and level fields defined
  const annotationsStyles = useSelector(selectAnnotationsStyles)
  const annotationFields = useSelector(selectAnnotationFields)
  const stateView = useSelector(selectViewSpec)
  const stateViewStyle = useSelector(selectViewStyleChart)
  const [selectionBoxExtent, setSelectionBoxExtent] = useState(null) // Handles openlayer DragBox Event (trigger on 'boxend') (search openlayer DragBox Event for useEffect)
  const tracker = useRef(null); // used to monitor events in MapWrapper component
  // Frame the map once; annotation edits must not recompute/refit the view
  const viewFramedRef = useRef(false);

  // set intial state - used to track references to OpenLayers 
  //  objects for use in hooks, event handlers, etc.
  const [ map, setMap ] = useState()
  const [ olLayers, setOlLayers] = useState(null) // null = not initialized yet (forces first rebuild)
  const [ layersLoaded, setLayersLoaded ] = useState(false)
  const [ drawingLayer, setDrawingLayer] = useState(new VectorLayer({}))
  const [ , setFeatures] = useState([])
  const [ featuresLayer, setFeaturesLayer ] = useState(new VectorLayer({}))
  const [  , setSelectedCoord ] = useState()
  
  // interactions used in the map
  const [interactions, setInteractions] = useState({
    drawLine: null, // handle linestrings
    drawPolygon: null, // handle polygons
    drawPoint: null, // handles points
    drawSquare: null, // handlesSqaures => not used in code (22/04/2025)
    selectSingleClick: null, // handle single object selection on click
	  selectionBox: null, // handle multiple selection with dragbox
	  modify: null, // handle annnotation edition using modify interaction
    partialDrawPostVertex: null, // handle partial redraw after the clicked vertex
    partialDrawPreVertex: null, // handle partial redraw before the clicked vertex
    translate: null, // handle translate interaction on annotations
    translateByTwoClicks: null, // two clicks from→to on current selection
    removePointsInExtent: null, // DragBox → remove line points in extent
    textLabel: null, // click the map to place a text label
  });

  const selectedFeaturesRef = useRef([]);
  const [selectedFeaturesRefState, setSelectedFeaturesRefState] = useState(null);
  const vertexFromSelectedFeaturesRef = useRef(null);
  const translateFeaturesRef = useRef(new Collection());
  
  const [ view, setView] = useState(new View({}))
  const [pointerMoveKey, setPointMoveKey] = useState()
  const [changeResolutionKey, setChangeResolutionKey] = useState() // change:resolution event management
  const [viewInformation, setViewInformation] = useState(null)
  const [sKeyPressed, setSKeyPressed] = useState(false); // "s" key pressing state => on  keyup set state to true to trigger selection handling
  const [eKeyPressed, setEKeyPressed] = useState(false); // "e" key pressing state => on keyup trigger edit handler
  const [mKeyPressed, setMKeyPressed] = useState(false); // "m" key pressing state => on keyup trigger move handler
  const [tKeyPressed, setTKeyPressed] = useState(false); // "t" key → translate by two clicks
  const [nKeyPressed, setNKeyPressed] = useState(false); // "n" key → New
  const [rKeyPressed, setRKeyPressed] = useState(false); // "r" key → remove points
  const [lKeyPressed, setLKeyPressed] = useState(false); // "l" key → toggle label selection
  // Fit button on the scale asks the map to frame the annotations
  const [fitAnnotationsTick, setFitAnnotationsTick] = useState(0);
  // the geometry of an annotation that must be saved. As soon as the newGeometry is filled (!= null), the annotation saving process will start
  // Once the annotation saved, newGeometry will go back to a "null" state
  const newGeometries = useSelector(getNewGeometries)
  // Background multi-geometry save (Move / Translate / Remove-points) — bottom snackbar progress
  const geometrySaveInFlightRef = useRef(false);
  // clears the translate modal inputs once the save attempt is finished
  const onTranslateSavedRef = useRef(null);
  const [geometrySaveSnack, setGeometrySaveSnack] = useState({
    open: false,
    running: false,
    severity: 'info',
    message: '',
  });
  // Information useful for the abort drawing function callback (when pressing ESC)
  const abortDrawingRef = useRef();

  // trigger by the text interaction
  // used to fill the label dialog
  const textLabelDraft = useSelector(selectTextLabelDraft);
  const textLabelDraftRef = useRef(null);
  textLabelDraftRef.current = textLabelDraft;
  // trigger by the select tool toggle
  // used so a click reads annotations or text labels
  const selectTarget = useSelector(selectAnnotationSelectTarget);
  const selectTargetRef = useRef("annotation");
  selectTargetRef.current = selectTarget;
  // trigger by the text dialog confirm
  // used to start the dedicated label save
  const [textToCreate, setTextToCreate] = useState(null);
  const [labelColorDefault, setLabelColorDefault] = useState('#000000');

  // Stock images loaded: key string (symbol or fill_pattern) => Image for styles
  const [loadedImages, setLoadedImages] = useState({});

  // Current position of the pointer in the map
  const [cursorPositionInMap, setCursorPositionInMap] = useState(null); // [x, y] in reference-units
  // Current resolution
  const [currentResolutionInMap, setCurrentResolutionInMap] = useState(null); // in in units/screen-pixels
  // default string is set here to avoid modifying multiple places if value is changed
  const defaultLabelString = "None"

  const moveViewToAnnotation = ({view,a}) => {
    if (!view || !a) {
      console.error('moveViewToAnnotation: missing view or annotation');
      return;
    }
    
    // Convert annotation to OpenLayers feature
    const feature = annotation2feature(a);
    const geometry = feature.getGeometry();
      
    // Calculate the center of the annotation's geometry
    const geometryExtent = geometry.getExtent();
    const geometryCenter = [
      (geometryExtent[0] + geometryExtent[2]) / 2,
      (geometryExtent[1] + geometryExtent[3]) / 2
    ];
      
    // Animate the view to center on the annotation (zoom level remains unchanged)
    view.animate({
      center: geometryCenter,
      duration: 300
    });
  }  
  
  // this useEffect handle the images for view keys fill_pattern and symbol parts
  // it download and the images necessary for the patterns
  useEffect(() => {
    if (!stateView) return;
  
    // handles images for symbology
    // https://codesandbox.io/p/sandbox/convert-file-to-base64-in-react-lqi1e?file=%2Fsrc%2FApp.js
    const loadImageAsImageObject = (url) => new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // handle CORS issues
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = make_generic_proxy_url(url, "file", authToken)
    });
  
    const fetchData = async () => {
      const allOptionsWithSymbology = stateView.keys
        .filter(key => key.type === "select")
        .flatMap(fk =>
          fk.options.map(o => ({
            ...o,
            keyName: fk.name,
            keyUid: fk.uid
          }))
        )
        .filter(o => o.color || o.fill_pattern || o.symbol) // accepting any key containig symbology elements
        .map(option => {
          // level is the key depth is the menu tree
          const level = stateView.keys.findIndex(k => k.uid === option.keyUid);
          return {
            keyName: option.keyName,
            keyUid: option.keyUid,
            id: option.id,
            level,
            ...(option.hasOwnProperty("color") ? { color: option.color } : {}),
            ...(option.hasOwnProperty("symbol") ? { symbol: option.symbol } : {}),
            ...(option.hasOwnProperty("fill_pattern") ? { fill_pattern: option.fill_pattern } : {}),
          };
        });
  
      // final style object stored in redux
      const styleDefinitions = {
        polygons: {},
        linestrings: {},
        points: {},
      };
  
      // New object to store loaded images by key string
      const imagesMap = {
        symbol: {},
        fill_pattern: {},
      };
  
      // Collect image loading promises to run them in parallel
      const imagePromises = [];
  
      for (const option of allOptionsWithSymbology) {
        const definition = {
          ...(option.hasOwnProperty("color") ? { color: option.color } : {}),
        };
  
        // get images or handle error
        if (option.hasOwnProperty("symbol")) {
          const symbolUrl = `s3://sites-orthophotos/symbology/symbols/${option.symbol}.png`;
          if (!imagesMap.symbol[option.symbol]) {
            const symbolPromise = loadImageAsImageObject(symbolUrl)
              .then(img => {
                imagesMap.symbol[option.symbol] = img; // store loaded Image
                definition.symbol = option.symbol; // keep only key here
              })
              .catch(err => {
                console.error(`Erreur chargement symbol: ${symbolUrl}`, err);
              });
            imagePromises.push(symbolPromise);
          } else {
            definition.symbol = option.symbol;
          }
        }
  
        if (option.hasOwnProperty("fill_pattern")) {
          const patternUrl = `s3://sites-orthophotos/symbology/fill_pattern/${option.fill_pattern}.png`;
          if (!imagesMap.fill_pattern[option.fill_pattern]) {
            const patternPromise = loadImageAsImageObject(patternUrl)
              .then(img => {
                imagesMap.fill_pattern[option.fill_pattern] = img; // store loaded Image
                definition.fill_pattern = option.fill_pattern; // keep only key here
              })
              .catch(err => {
                console.error(`Erreur chargement fill_pattern: ${patternUrl}`, err);
              });
            imagePromises.push(patternPromise);
          } else {
            definition.fill_pattern = option.fill_pattern;
          }
        }
  
        styleDefinitions.polygons[option.id] = definition;
        styleDefinitions.linestrings[option.id] = definition;
        styleDefinitions.points[option.id] = definition;
      }
  
      // Wait for all images to load in parallel
      await Promise.all(imagePromises);
      setLoadedImages(imagesMap);

      dispatch(setViewAnnotationStyles(styleDefinitions));
    };
  
    // 
    fetchData();
  }, [stateView]);
  


  // Activity tracking
  useEffect(() => {
    // map && view checks are needed
    if (currentUserId != null && tracker.current === null && map != null && view.getCenter()) {
      tracker.current = new ActivityTracker({
        metadata: {},
        event_min_duration: 2, 
        sending_frequency : 15,
        sending_function: async (infos, events) => {
          let other_infos = {
            ...infos,
            inspectionId: inspectionId,
            viewId: viewId,
            component: "MapWrapper",
            channel: window.orthophoto_bc_channel,
            events: events,
          }
          return apiClient.create_user_event({
            userId: currentUserId,
            category: "activity_tracking",
            other_infos: other_infos
          });
        }
      })
      tracker.current.start_tracking_html_element(document.body);
    }
  }, [currentUserId, map, view]);

  // color tools
  const pickGradientColor = (k) => {
    return [
      annotationsStyles.appStyleChart.gradient.min[0] + k * (annotationsStyles.appStyleChart.gradient.max[0] - annotationsStyles.appStyleChart.gradient.min[0]),
      annotationsStyles.appStyleChart.gradient.min[1] + k * (annotationsStyles.appStyleChart.gradient.max[1] - annotationsStyles.appStyleChart.gradient.min[1]),
      annotationsStyles.appStyleChart.gradient.min[2] + k * (annotationsStyles.appStyleChart.gradient.max[2] - annotationsStyles.appStyleChart.gradient.min[2])
    ]
  }

  // Available geometryName : 
  // - square : a square centered on the given point
  // - point : a point
  // - a polygon : 

  function annotation2feature(a){

    switch(a.geometryName){
      case "square":
        // get the ol coord
        const colCoordSquare = reference_to_openlayer_coordinates(a.geometry.coordinates, viewInformation)
        // in this case the feature are "Point" but we will draw squares
        return new Feature({
          geometry: new Polygon([[
            [colCoordSquare[0] - 128,colCoordSquare[1] - 128],
            [colCoordSquare[0] - 128,colCoordSquare[1] + 128],
            [colCoordSquare[0] + 128,colCoordSquare[1] + 128],
            [colCoordSquare[0] + 128,colCoordSquare[1] - 128],
            [colCoordSquare[0] - 128,colCoordSquare[1] - 128]
          ]]),
          annotation: a
        })
      case "point":
        // get the ol coord
        const colCoordPoint = reference_to_openlayer_coordinates(a.geometry.coordinates, viewInformation)
        // in this case the feature are "Point" but we will draw squares
        return new Feature({
          geometry: new Point(colCoordPoint),
          annotation: a
        })
      case "polygon":
        // get the ol coord
        const colCoordPolygon = a.geometry.coordinates.map((c) => reference_to_openlayer_coordinates(c, viewInformation))
        // in this case the feature are "Point" but we will draw squares
        return new Feature({
          geometry: new Polygon([colCoordPolygon]),
          annotation: a
        })
      case "line":
        // get the ol coord
        const colCoordLine = a.geometry.coordinates.map((c) => reference_to_openlayer_coordinates(c, viewInformation))
        // in this case the feature are "Point" but we will draw squares
        return new Feature({
          geometry: new LineString(colCoordLine),
          annotation: a
        })
      default:
        // nothing
    }
  }

  // create state ref that can be accessed in OpenLayers onclick callback function
  //  https://stackoverflow.com/a/60643670
  const mapRef = useRef()
  mapRef.current = map
    
  // get ref to div element - OpenLayers will render into this div
  const mapElement = useRef()

  // func used to update color in png 
  function recolorImage(img, newColor) {
    // create image using canvas then update it
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
  
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
  
    // data is an array with 4 index per pixel for R/G/B/A
    for (let i = 0; i < data.length; i += 4) {
      // if alpha is not set to 0 then update color
      if (data[i + 3] > 0) {
        data[i] = newColor[0];
        data[i + 1] = newColor[1];
        data[i + 2] = newColor[2];
      }
    }
  
    ctx.putImageData(imageData, 0, 0);
    return canvas;
  }
  
  // handle symbol image resize to fit in the canva
  function resizeAndCenterImage(img, targetSize) {
    const canvas = document.createElement('canvas');
    canvas.width = targetSize;
    canvas.height = targetSize;
    const ctx = canvas.getContext('2d');
  
    const ratio = Math.min(targetSize / img.width, targetSize / img.height);
    const drawWidth = img.width * ratio;
    const drawHeight = img.height * ratio;
    const offsetX = (targetSize - drawWidth) / 2;
    const offsetY = (targetSize - drawHeight) / 2;
  
    ctx.clearRect(0, 0, targetSize, targetSize);
    ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
  
    return canvas;
  }

  
  // set up basic canvas with given size
  function createCleanCanvas(size) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, size, size);
    return {canvas, ctx}
  }

  // handle annotation display style
  // the fun return an array of styles depending on various conditions:
  //  - is annotation selected ?
  //  - is there a style linked to the view for this annotation ?
  //  - the geomtry type of the annotation
  // this func also handle label display on annotations
  const featureStyleFunction = ({ fill, stroke, selectedStyle, label, viewStyleChart, loadedImages }) => {
    // Cache colored pattern and symbol
    let cache_fill_pattern = {};
    function get_fill_pattern(loadedImages, pattern_name, color, size){
      // Compute if necessary
      if (cache_fill_pattern[pattern_name+color+size] == null){
        const { canvas, ctx } = createCleanCanvas(size);
        const img = loadedImages["fill_pattern"][pattern_name];
        // handle color update for the pattern
        const recoloredCanvas = recolorImage(img, color);
        cache_fill_pattern[pattern_name+color+size] = ctx.createPattern(recoloredCanvas, 'repeat');
      }
      // return pattern from cache
      return cache_fill_pattern[pattern_name+color+size];
    }
    let cache_symbols = {};
    function get_symbol(loadedImages, symbol_name, color, size){
      // Compute if necessary
      if (cache_symbols[symbol_name+color+size] == null){
        const recoloredCanvas = recolorImage(loadedImages["symbol"][symbol_name], color);
        // fit symbol image to canvas size ensuring we ssee the complete symbol
        cache_symbols[symbol_name+color+size] = resizeAndCenterImage(recoloredCanvas, size);
      }
      // return pattern from cache
      return cache_symbols[symbol_name+color+size];
    }

    // Then function that will be called by OpenLayer for each feature
    return function (feature) {
      // get annotation datas
      const a = feature.getProperties().annotation;
      // trigger by a text annotation on the map
      // used to draw its label
      if (a?.text?.value) {
        const isSelected = selectedAnnotations.includes(a.id);
        return new Style({
          zIndex: 100,
          image: new Circle({
            radius: 8,
            fill: new Fill({ color: isSelected ? 'rgba(251, 118, 88, 0.45)' : 'rgba(0, 0, 0, 0.01)' }),
          }),
          text: new Text({
            text: String(a.text.value),
            // trigger by a label with a line break, used to line the lines up on the left of the point
            textAlign: String(a.text.value).includes('\n') ? 'left' : 'center',
            font: 'bold 14px sans-serif',
            fill: new Fill({ color: isSelected ? '#FB7658' : (a.text.color || '#000000') }),
            stroke: new Stroke({ color: isSelected ? '#000000' : '#ffffff', width: isSelected ? 4 : 3 }),
            overflow: true,
            padding: [6, 8, 6, 8],
          }),
        });
      }
      const geometry = feature.getGeometry();
      const geomType = geometry.getType();
      const isSelected = selectedAnnotations.includes(a.id);
      const symbology = getAnnotationSymbology(a, coloredOptionsFromKeys);
      const styles = [];

      const haloColor = selectedStyle.halo?.color ?? [255, 255, 255, 0.95];
      const haloWidth = selectedStyle.halo?.width ?? 8;

      const makeStroke = (color, normalWidth = 2) => {
        if (isSelected) {
          return new Stroke({
            color,
            width: selectedStyle.stroke?.width ?? 3,
            ...(selectedStyle.stroke?.lineDash
              ? { lineDash: selectedStyle.stroke.lineDash }
              : {}),
          });
        }
        return new Stroke({ color, width: normalWidth });
      };

      const makeHaloStroke = () =>
        new Stroke({ color: haloColor, width: haloWidth });
  
      // Defaults
      const defaultFill = new Fill(fill || { color: [128, 128, 128, 0.4] });
      const defaultStrokeColor = stroke?.color ?? [255, 165, 100, 1];
      const defaultStroke = makeStroke(defaultStrokeColor, stroke?.width ?? 2);
  
      // build base for fill/stroke/image used in 
      let baseFill = defaultFill;
      let baseStroke = defaultStroke;
      let baseImage = null;
  
      let color = defaultStrokeColor;

      // Try get style from viewStyleChart if any
      if (viewStyleChart && symbology && a.data[symbology.keyName]) {
        const geomKey = geomType.toLowerCase() + 's';
        const styleDef = symbology;
        if (styleDef) {
          const resolution = view.getResolution();

          const sizeMeters = 0.5; // size in meters
          const sizePixels = sizeMeters / resolution; // meters to pixels

          const size = geomType === polygonClassName ? sizePixels : 24;
          color = styleDef.color;

          if (geomType === polygonClassName) {
            // if there is a fill_pattern => get the image add it in a canvas, update it's color and add it to style fill
            if (styleDef.hasOwnProperty("fill_pattern")) {
              if (loadedImages && loadedImages["fill_pattern"][styleDef.fill_pattern]) {
                const pattern = get_fill_pattern(loadedImages, styleDef.fill_pattern, color, size);
  
                // add pattern to the annotation background
                if (pattern) {
                  baseFill = new Fill({ color: pattern });
                } else {
                  baseFill = new Fill({ color });
                }
              }
            } else {
              // No fill_pattern => "empty" fill
              baseFill = new Fill({ color: 'rgba(0, 0, 0, 0)' });
            }
  
            baseStroke = makeStroke(color);
          }
  
          else if (geomType === pointClassName) {
            // if there is a symbol => get the image add it in a canvas, update it's color and add it to style icon
            if (styleDef.hasOwnProperty("symbol") && loadedImages && loadedImages["symbol"][styleDef.symbol]) {
              const fittedCanvas = get_symbol(loadedImages, styleDef.symbol, color, size);

              baseImage = new Icon({
                img: fittedCanvas,
                imgSize: [size, size],
                anchor: [0.5, 0.5],  // center cnavas on point coords
              });
            } else { // add a simple dot when no given symbol
              baseImage = new Circle({
                radius: 4,
                fill: new Fill({ color: 'rgba(0,0,0,0)' }),
                stroke: makeStroke(color),
              });
            }
          
            baseFill = new Fill({ color });
            baseStroke = makeStroke(color);
          }
  
          else if (geomType === lineStringClassName) {
            baseFill = new Fill({ color });
            baseStroke = makeStroke(color);
  
            // A/B endpoints in Edit mode (all edit sub-states) so partial redraw choices are identifiable
            const isAnnotationEditMode =
              annotationState === "SelectionOnGoing" ||
              annotationState === "EditOnGoing" ||
              annotationState === "AnnotationRedrawOnGoing" ||
              annotationState === "AnnotationModifyOnGoing" ||
              annotationState === "AnnotationPostVertexEditOnGoing" ||
              annotationState === "AnnotationPreVertexEditOnGoing" ||
              (annotationState === "DrawOnGoing" && annotationsForGeometryEdit.length > 0);
            if (isSelected && a.geometryName === 'line' && isAnnotationEditMode) {
              const coords = a.geometry?.coordinates;
              if (coords?.length >= 2) {
                styles.push(
                  new Style({
                    geometry: new Point(coords[0]),
                    text: new Text({ text: 'A', font: 'bold 12px sans-serif', fill: new Fill({ color: 'red' }), offsetY: -15 }),
                  }),
                  new Style({
                    geometry: new Point(coords[coords.length - 1]),
                    text: new Text({ text: 'B', font: 'bold 12px sans-serif', fill: new Fill({ color: 'blue' }), offsetY: -15 }),
                  })
                );
              }
            }
          }
  
          else {
            baseFill = new Fill({ color });
            baseStroke = makeStroke(color);
          }
        }
      }
  
      const mainStyle = new Style({
        image: geomType === pointClassName
          ? (baseImage || new Circle({ radius: 4, fill: baseFill, stroke: baseStroke }))
          : undefined,
        fill: geomType !== pointClassName ? baseFill : undefined,
        stroke: geomType !== pointClassName ? baseStroke : undefined,
      });

      if (isSelected) {
        if (geomType === pointClassName) {
          const resolution = view.getResolution();
          const sizePixels = 0.5 / resolution;
          const symbolRadius =
            baseImage && symbology?.symbol ? Math.max(sizePixels / 2, 12) + 6 : 10;
          styles.push(
            new Style({
              image: new Circle({
                radius: symbolRadius,
                fill: new Fill({ color: 'rgba(0,0,0,0)' }),
                stroke: makeHaloStroke(),
              }),
            })
          );
        } else {
          styles.push(new Style({ stroke: makeHaloStroke() }));
        }
      }

      styles.push(mainStyle);
  
      // handle labl feature display 
      if (label && label !== defaultLabelString) {
        const key = annotationFields.keys.find((k) => k.name === label);
        const value = get_display_value(key, a, stateView.keys);
        const labelText = new Text({
          text: value,
          font: 'bold 14px sans-serif',
          fill: new Fill({ color: [255, 255, 255, 1] }),
          stroke: new Stroke({ color: [0, 0, 0, 1], width: 5 }),
          overflow: true,
          offsetY: 20,
        });
  
        styles.push(new Style({ text: labelText }));
      }
  
      return styles;
    };
  };
  
  

  

  // Create a new OpenLayer "orthophoto" tile layer
  const newTileLayer = ({sizeInPixels, tfw_adbecf, maxZoom, url}) => {
    // Compute orthophoto bottom-left and top-right corners in the reference coordinate frame:
    let ref_bottom_left = [
      tfw_adbecf[0] * 0 + tfw_adbecf[2] * sizeInPixels[1] + tfw_adbecf[4],
      //   a                  b                                 c          
      tfw_adbecf[1] * 0 + tfw_adbecf[3] * sizeInPixels[1] + tfw_adbecf[5],
      //   d                  e                                  f
    ];
    let ref_top_right = [
      tfw_adbecf[0] * sizeInPixels[0] + tfw_adbecf[2] * 0 + tfw_adbecf[4],
      tfw_adbecf[1] * sizeInPixels[0] + tfw_adbecf[3] * 0 + tfw_adbecf[5],
    ];

    // Convert to OpenLayer conventions:
    let ol_bottom_left = reference_to_openlayer_coordinates(ref_bottom_left, viewInformation);
    let ol_top_right = reference_to_openlayer_coordinates(ref_top_right, viewInformation);

    const computed_extent = [ // tiles outside of the extent (defined as a [minx, miny, maxx, maxy] bbox) won't be downloaded
    ol_bottom_left[0], 
    ol_bottom_left[1], 
    ol_top_right[0], 
    ol_top_right[1], 
    ]

    return new TileLayer({
      source: new XYZ({
        url: url,
        crossOrigin: 'anonymous',
        tileGrid: new TileGrid({
          extent: computed_extent,
          // minimum Z, any tile with a z < minZoom won't be download
          minZoom: 0, 
          // the position in the openlayer coordinate system of the topleft of the [x=0, y=0, z] tiles
          origin: reference_to_openlayer_coordinates([tfw_adbecf[4],tfw_adbecf[5]], viewInformation),
          // Resolution in units/pixel for each Z. At a given Z, equivalent of the size in units of 1 pixel of a tile
          resolutions: [...Array(maxZoom+1).keys()].map((z) => {
            return 2**(maxZoom-z)*Math.abs(tfw_adbecf[0]);
          }), 
          // size in PX
          tileSize: [256, 256]
        })

      }),
      extent: computed_extent
    })
  }

  // Style for photos
  const squareStyle = new Style({
    image: new RegularShape({
      points: 4, // Creates a square
      radius: 10, // Half the side length
      angle: Math.PI / 4, // Rotates to look like a square
      fill: new Fill({ color: 'rgb(123, 164, 229)' }),
      stroke: new Stroke({ color: 'rgb(33, 84, 165)', width: 1 }),
    }),
  });

  // Create a new OpenLayer "photos" layer
  const newPhotoLayer = ({photos}) => {
    // Check Photo format
    photos = photos.filter((photo) => {
      if (photo.geometry.type === "Point") return true;
      else {
        console.log("Unexpected: ")
        console.log(photo);
        return false;
      }
    })
    console.log("Nb de photos: " + photos.length)
    // Create OpenLayer layer
    const newFeaturesLayer = new VectorLayer({
      source: new VectorSource({
        features: photos.map((photo) => {
          // Create feature
          const feature = new Feature({
            geometry: new Point(photo.geometry.coordinates),
            photo: photo
          });
          // Create a square style
          
          // Set style
          feature.setStyle(squareStyle);
          return feature;
        })
      })
    });
    return newFeaturesLayer;
  }


  const pointerMoveAction = (view) => (e) => {
    if(viewInformation){
      // Get position in the reference coordinate system
      const positionInMap = openlayer_to_reference_coordinates(mapRef.current.getCoordinateFromPixel(e.pixel), viewInformation)
      setCursorPositionInMap(positionInMap)
      setCurrentResolutionInMap(view.getResolution())
    }
  }

  // What to do when the resolution changes
  const resolutionChangeAction = (view) => (e) => {
    if(viewInformation){
      setCurrentResolutionInMap(view.getResolution())
    }
  }

  // add annotation when draw is ended (geometry is updated on drawend events)
  const validateModifyOnAnnotation = useSelector(getValidateModifyOnAnnotation);
  const enterListenerRef = useRef(null); // Pour stocker la fonction du listener

  useEffect(() => {
    // handle listener on enter to allow save on modify using the enter key
    // note the following infos:
    //  - newGeometries is a list geometry objects containin the annotation.geometry object
    //  - for features targeting one annotaiton at a time like: modify, drawPodtVertex
    //    we lock newGeometries.length === 1 to ensure there is only one annotation
    //  - annotationsForGeometryEdit: list of ids used to edit geometries with updateAnnotationApi
    if (
      annotationState === "AnnotationModifyOnGoing" &&
      newGeometries.length === 1 &&
      annotationsForGeometryEdit.length === 1 &&
      enterListenerRef.current === null
    ) {
      const handleEnterKey = (e) => {
        if (e.key === "Enter") {
          dispatch(setValidateModifyOnAnnotation(true));
        }
      };

      enterListenerRef.current = handleEnterKey;
      document.addEventListener("keydown", handleEnterKey);
    }
    // updating annotation triggered by a modify interaction
    if (
      validateModifyOnAnnotation &&
      annotationState === "AnnotationModifyOnGoing" &&
      newGeometries.length === 1 &&
      annotationsForGeometryEdit.length > 0
    ) {
      updateAnnotationApi({
        ids: annotationsForGeometryEdit,
        ...newGeometries[0],
        inspectionId,
      }, (result) => {
        dispatch(setNewGeometries([]));
        if (result === null) {
          dispatch(updateAnnotationState("AnnotationEditionRejected"));
          dispatch(setAnnotationsForGeometryEdit([]));
        } else {
          dispatch(updateAnnotation(result[0]));
          dispatch(setAnnotationsForGeometryEdit([]));
          dispatch(updateAnnotationState("AnnotationEdited"));
          console.log("Annotation updated via validation");
        }
        dispatch(setValidateModifyOnAnnotation(false));
        enterListenerRef.current = null
      });
    }

    // updating annotation triggered by any other interactions (incl. Move / Translate multi)
    else if (
      !geometrySaveInFlightRef.current &&
      newGeometries.length > 0 &&
      annotationsForGeometryEdit.length === newGeometries.length &&
      annotationState !== "AnnotationModifyOnGoing"
    ) {
      const isTranslate =
        annotationState === "AnnotationsTranslationOnGoing" ||
        annotationState === "AnnotationsTranslateByTwoClicksOnGoing";

      const payload = annotationsForGeometryEdit.map((id, i) => ({ id, ...newGeometries[i] }));
      const total = payload.length;
      let doneCount = 0;

      geometrySaveInFlightRef.current = true;
      // Visual feedback is optional / independent from runInParallel
      setGeometrySaveSnack({
        open: true,
        running: true,
        severity: 'info',
        message: `Saving 0 / ${total}…`,
      });

      runInParallel(payload, GEOMETRY_SAVE_PARALLEL, ({ id, ...geom }) =>
        new Promise((ok, ko) => {
          updateAnnotationApi({ ids: [id], ...geom, inspectionId }, (r) => {
            if (!r) return ko(new Error('fail'));
            doneCount += 1;
            setGeometrySaveSnack({
              open: true,
              running: true,
              severity: 'info',
              message: `Saving ${doneCount} / ${total}…`,
            });
            ok(r);
          });
        }),
      )
        .then((results) => {
          dispatch(setNewGeometries([]));
          dispatch(setAnnotationsForGeometryEdit([]));
          // updateAnnotations send back [[ann], [ann], [ann]]
          // so we need to flatten the results to get: [ann, ann, ann]
          results.flat().forEach((r) => {
            // trigger by a label in this save
            // used to keep it in the text list, the edit response includes text
            if (r.text?.value) {
              dispatch(updateTextAnnotation(r));
              return;
            }
            dispatch(updateAnnotation(r));
          });
          onTranslateSavedRef.current?.();
          onTranslateSavedRef.current = null;
          if (!isTranslate) dispatch(updateAnnotationState("AnnotationEdited"));
          setGeometrySaveSnack({
            open: true,
            running: false,
            severity: 'success',
            message: `Saved ${total} annotation(s).`,
          });
          console.log("Annotation updated successfully");
        })
        .catch(() => {
          dispatch(setNewGeometries([]));
          dispatch(setAnnotationsForGeometryEdit([]));
          setGeometrySaveSnack({
            open: true,
            running: false,
            severity: 'error',
            message: `Executed ${doneCount} / ${total}. Save failed.${isTranslate ? ' The annotation has returned to its original position.' : ''}`,
          });
          onTranslateSavedRef.current?.();
          onTranslateSavedRef.current = null;
          if (!isTranslate) dispatch(updateAnnotationState("AnnotationEditionRejected"));
        })
        .finally(() => {
          geometrySaveInFlightRef.current = false;
        });
    }
  
    // Create a new Annotation
    else if (
      newGeometries.length === 1 &&
      annotationsForGeometryEdit.length === 0
    ) {
      console.log("check create")
      console.log(annotationState)
      console.log(annotationsForGeometryEdit)
      dispatch(updateAnnotationState("AwaitingCreateValidation"));
      createAnnotationApi({
        ...newGeometries[0],
        inspectionId,
        viewId,
      }, (result) => {
        dispatch(setNewGeometries([]));
        if (result === null) {
          dispatch(updateAnnotationState("AnnotationRejected"));
        } else {
          dispatch(createAnnotation(result));
          dispatch(updateAnnotationState("AnnotationCreated"));
        }
      });
    }
  
    // handles removal of listeners 
    if (newGeometries.length === 1) {
      dispatch(setValidateModifyOnAnnotation(false));
      window.removeEventListener("keydown", enterListenerRef.current);
      enterListenerRef.current = null
    }
  }, [validateModifyOnAnnotation, newGeometries]);

  // trigger by the text dialog confirm
  // used to save the label through createAnnotation, into the text state
  useEffect(() => {
    if (!textToCreate || !textLabelDraft) return;
    const coordinates = textLabelDraft;
    const text = textToCreate;
    dispatch(setTextLabelDraft(null));
    createAnnotationOnAPI({
      viewId,
      inspectionId,
      geometryName: "point",
      geometry: { type: "Point", coordinates },
      text,
    }).then((result) => {
      dispatch(createTextAnnotation(result));
      setTextToCreate(null);
    }).catch(() => {
      setTextToCreate(null);
    });
  }, [textToCreate]);

  // unmount
  useEffect(() => {
    return () => {
      if (tracker.current != null) {
        // on component unmount (meaning component stopped being used), stop tracking
        tracker.current.stop_tracking_html_element();
        tracker.current = null
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  // Load annotations independently of map/ortho rebuild (required when there is no orthophoto)
  useEffect(() => {
    if (!viewId || !inspectionId) return;
    annotationsApi.index({ viewId, inspectionId }, ({ view, annotations: loaded }) => {
      dispatch(updateViewSpec(view));
      const withCenter = loaded.filter((a) => a.position?.center);
      // trigger by the annotation index
      // used to keep labels out of the annotation list
      dispatch(replaceTextAnnotations(withCenter.filter((a) => a?.text?.value)));
      dispatch(replaceAnnotations(withCenter.filter((a) => !a?.text?.value)));
    });
  }, [viewId, inspectionId, dispatch]);

  // initialize map on first render - logic formerly put into componentDidMount
  useEffect( () => {
    // Edit 2024-02-13: No feature when there is no map and no reference frame
    // compute features from annotations
    //const initialFeatures = annotations.map(annotation2feature)
    //setFeatures(initialFeatures)


    const initialView = new View({})

    // create map
    const initialMap = new Map({
      controls: [],
      target: mapElement.current,
      layers: [],
      view: initialView
    })

    // save map and vector layer references to state
    setMap(initialMap)

    // setFeaturesLayer(initalFeaturesLayer)

    // load the layers
    layersApi.index(inspectionId,(layers) => {
      // Sort layers by 1) type (so that photo are above orthophoto) and 2) anti-alphabetical order:
      layers.sort(function (a, b) {
        if (a.type < b.type) return -1;
        else if (a.type > b.type) return 1;
        else if (a.name < b.name) return 1;
        else if (a.name > b.name) return -1;
        else return 0;
      });
      // Save
      setOlLayers(null);
      dispatch(setLayers({layers}))
      setLayersLoaded(true)
    })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  // Update the list of OpenLayer "vectors" depending on the annotation to be displayed:
  // ie what's in "annotations" except the possible annotation whose geometry is being edited (annotationsForGeometryEdit)
  useEffect(() => {
    // Convert annotation geometry to OpenLayer geometries
    let newFeatures = [];

    // viewInformation must be defined so that conversion to OpenLayer coordinate frame can work
    if (viewInformation != null && featuresLayer != null) {
      let featureFromModify = null;
      let filteredAnnotations = annotations;

      // Modify interaction: handle geometry modification using OpenLayers Modify interaction
      if (selectedFeaturesRef.current.length === 1 && annotationState === "AnnotationModifyOnGoing") {
        const modifiedFeature = selectedFeaturesRef.current[0];
        const modifiedFeatureId = modifiedFeature.getProperties().annotation.id; 
        
        filteredAnnotations = annotations.filter(el => el.id !== modifiedFeatureId)
        // Add the modified feature in newFeatures
        featureFromModify = modifiedFeature
      }

      // Redraw interaction: handle complete geometry redraw (exclude from normal rendering)
      if (annotationsForGeometryEdit.length === 1 && annotationState === "AnnotationRedrawOnGoing") {
        filteredAnnotations = annotations.filter(el => el.id !== annotationsForGeometryEdit[0]);
      }

      // Partial redraw interactions: handle partial geometry redraw (before/after vertex)
      if (annotationsForGeometryEdit.length === 1 && 
        (
          annotationState === "AnnotationPostVertexEditOnGoing" ||
          annotationState === "AnnotationPreVertexEditOnGoing"
        )) {

        const annotation = annotations.filter(el => el.id === selectedAnnotations[0])[0];
        // passing selected annotation to ref to make it accessible for interactions
        selectedFeaturesRef.current = [annotation2feature(annotation)]
        setSelectedFeaturesRefState(selectedFeaturesRef.current[0])
      }
      
      // Optimistic rendering while a DB save is pending
      // hide features that are being edited while saving
      // (same signal as the save effect: edit ids + matching newGeometries)
      const pendingGeometry = (
        annotationsForGeometryEdit.length > 0 &&
        newGeometries?.length === annotationsForGeometryEdit.length
      ) ? new Map(annotationsForGeometryEdit.map((id, i) => [id, newGeometries[i]])) : null;
      const featureWithPendingGeometry = (a) => {
        const pending = pendingGeometry?.get(a.id);
        if (!pending) return annotation2feature(a);
        return annotation2feature({
          ...a,
          geometry: pending.geometry,
          geometryName: pending.geometryName,
        });
      };
      // Normal rendering: convert annotations to features without optimistic override
      newFeatures = filteredAnnotations.map(featureWithPendingGeometry);
      // trigger by a label move still waiting on the save
      // used to keep that label at its new position on the map
      newFeatures = [...newFeatures, ...textAnnotations.map(featureWithPendingGeometry)];
      
      if (featureFromModify !== null) {
        newFeatures.push(featureFromModify)
        featureFromModify = null;
      }
      
      // Save features in the state and update the OpenLayer Vector layer
      setFeatures(newFeatures);
      featuresLayer.setSource(
          new VectorSource({
              features: newFeatures
          })
      );
    }
  }, [annotations, textAnnotations, annotationsForGeometryEdit, viewInformation, annotationState, newGeometries, featuresLayer]);



  useEffect(() => {
    // update features style when either style or selected annotations are updated
    // app style is used as default
    featuresLayer.setStyle(featureStyleFunction({
      ...annotationsStyles.appStyleChart.feature,
      selectedStyle: annotationsStyles.appStyleChart.selected,
      label: annotationsStyles.label,
      viewStyleChart: stateViewStyle,
      loadedImages,
    }))
  },[coloredOptionsFromKeys, annotationsStyles, selectedAnnotations, loadedImages, annotationState, annotationsForGeometryEdit])

  // Handles openlayer DragBox Event (trigger on 'boxend')
  // on trigger either:
  //  - Select if features are not or partially in the selectedFeatures already
  //  - Unselect if features totally on the selectedFeatures
  useEffect(() => {
    if (selectionBoxExtent != null) {
      const wantText = selectTargetRef.current === "text";
      const boxFeatures = featuresLayer.getSource().getFeaturesInExtent(selectionBoxExtent).filter((f) => {
        const a = f.getProperties()?.annotation;
        if (!a?.id) return false;
        const isText = Boolean(a.text?.value);
        return wantText ? isText : !isText;
      });
      // check if the whole dragBox content already belongs to selected elements
      const boxFeatureIds = boxFeatures.map((f) => f.values_.annotation.id);
      const allSelected = boxFeatureIds.every(id => selectedAnnotations.includes(id));

      // handles selection for modify
      if (boxFeatureIds.length === 1) {
        selectedFeaturesRef.current = [boxFeatures[0]];
      } else {
        selectedFeaturesRef.current = [];
      }

      if (allSelected) {
        // remove features from selectedAnnotations
        dispatch(selectMultipleAnnotations(selectedAnnotations.filter(id => !boxFeatureIds.includes(id))))
      } else {
        // using set to get unique values from the 2 arrays
        dispatch(selectMultipleAnnotations([
          ...new Set([
            ...selectedAnnotations,
            ...boxFeatureIds
          ])
        ]));
      }
    }
  }, [selectionBoxExtent]);

  const equalsIgnoreOrder = (a, b) => {
    if (a.length !== b.length) return false;
    const uniqueValues = new Set([...a, ...b]);
    for (const v of uniqueValues) {
      const aCount = a.filter(e => e === v).length;
      const bCount = b.filter(e => e === v).length;
      if (aCount !== bCount) return false;
    }
    return true;
  }

  // Reset framing / layers when switching inspection
  useEffect(() => {
    viewFramedRef.current = false;
    setViewInformation(null);
    setOlLayers(null);
  }, [inspectionId]);

  // Compute framing once (ortho from TFW, or no-ortho from annotations). After that, annotations
  // can change freely without touching the view.
  useEffect(() => {
    if (viewFramedRef.current) return;
    // Wait for layers API before deciding "no ortho"
    if (!layersLoaded && stateLayers.length === 0) return;

    const hasOrtho = stateLayers.some((l) => l.type === "orthophoto");
    // No-ortho: wait for annotations so we don't lock on the placeholder extent
    if (!hasOrtho && annotations.length === 0) return;

    setViewInformation(computeViewInformation(stateLayers, annotations));
  }, [stateLayers, layersLoaded, annotations]);

  // Get the view information and set the view on the map
  // 2 cases:
  // 1. if there is no ortho, fit the map to the hardcoded extent
  // 2. if there is ortho, fit the map to the annotations
  useEffect(() => {
    if (!map || !viewInformation || viewFramedRef.current) return;

    // set the view information on the map
    map.set('x_left_right', viewInformation.x_left_right);
    map.set('y_bottom_up', viewInformation.y_bottom_up);
    map.set('global_extent', viewInformation.extent);

    // init a new view with the view information
    const newView = new View({
      resolution: viewInformation.resolution,
      center: viewInformation.center,
      minResolution: viewInformation.minResolution,
      maxResolution: viewInformation.maxResolution,
      showFullExtent: true,
      extent: viewInformation.extent
    });
    map.setView(newView);
    setView(newView);

    // case no ortho: fit the map to a calculated extent 
    const noOrtho = !stateLayers.some((l) => l.type === "orthophoto");
    // based on annotations coordinates
    // handled manually here cause map.size is not available on view init
    if (noOrtho && map.getSize()) {
      newView.fit(viewInformation.extent, fitMapOptions(map));
    }

    setCurrentResolutionInMap(newView.getResolution());
    // Activity tracking: keep current visible bbox for user-activity events
    newView.on("change", () => {
      if (map !== null && tracker.current !== null) {
        const bbox = newView.calculateExtent(map.getSize());
        tracker.current.saveViewBbox(bbox);
      }
    });

    viewFramedRef.current = true;
  }, [map, viewInformation, stateLayers]);

  // Same layer compare as main (avoid full map re-init). No ortho → mapLayers may be empty,
  // so olLayers starts null and we also require selectSingleClick before treating as "already inited".
  useEffect(() => {
    if ((map != undefined) && (viewInformation != undefined) && (stateLayers != undefined) && (authToken != "")){
      const hasOrtho = stateLayers.some((l) => l.type === "orthophoto");
      const mapLayers = stateLayers.filter((l) => l.type === "orthophoto" || (hasOrtho && l.type === "photo"));
      // NEED TO BE ABLE TO COMPARE CURRENT OL LAYERS AND NEW LAYERS :'(
      const stateLayerIds = mapLayers.map(({ id }) => id);
      const olLayerIds = olLayers == null ? null : Object.keys(olLayers);
      if (olLayerIds != null && equalsIgnoreOrder(stateLayerIds, olLayerIds) && interactions.selectSingleClick != null) {
        // no change in layers
        mapLayers.forEach(({ id, selected }) => {
          const thisLayer = olLayers[id];
          if (thisLayer !== undefined) {
            thisLayer.setVisible(selected);
          }
        });
      } else {
        inspectionApi.get(inspectionId, (inspection) => {
          dispatch(setInspection({inspection}))
          dispatch(setTopbar({
            title: inspection.name
          }))
        })

        // new layers, need to reset the map
        map.getLayers().forEach((l) => {
          map.removeLayer(l)
        })
        const newView = map.getView();

        let newOlLayers = {}
        for (const layer of mapLayers){
          if (layer.type === "orthophoto"){
            newOlLayers[layer.id] = newTileLayer({
              sizeInPixels: layer.sizeInPixels,
              tfw_adbecf: layer.tfw_adbecf,
              maxZoom: layer.maxZoom,
              url: make_generic_proxy_url(layer.url, "parent_folder", authToken, {"layerId": layer.id})
            })
            map.addLayer(newOlLayers[layer.id])
          }
          else if (layer.type === "photo"){
            newOlLayers[layer.id] = newPhotoLayer({
              photos: layer.photos.map((photo) => {
                return {
                  ...photo,
                  url: make_generic_proxy_url(photo.url, "file", authToken, {"layerId": layer.id, "photoId": photo.id})
                }
              })
            })
            map.addLayer(newOlLayers[layer.id])
          }
          else{
            console.log("Unexpected: " + layer.type);
          }
        }

        // update this state component
        setOlLayers(newOlLayers)

        const styleFunction = function (geometryName = undefined){
          return (feature) => {
            let gName = geometryName
            if(gName === undefined){
              gName = feature.getGeometryName()
            }
            const geometry = feature.getGeometry();
            const styles = [];
            switch(gName){
              case "square":
                return [
                  new Style({
                    image: new RegularShape({
                      scale: 1/Math.pow(2,newView.getMaxZoom()-newView.getZoom()),
                      points: 4,
                      angle: Math.PI/4,
                      radius: 181,
                      fill: new Fill(annotationsStyles.appStyleChart.candidateAnnotation.fill),
                      stroke: new Stroke(annotationsStyles.appStyleChart.candidateAnnotation.stroke),
                    })
                  })
                ]

              case "point":
                styles.push(
                              // linestring
                  new Style({
                    image: new Circle({
                      radius: 4,
                      fill:  new Fill(annotationsStyles.appStyleChart.candidateAnnotation.fill),
                      stroke: new Stroke(annotationsStyles.appStyleChart.candidateAnnotation.stroke),
                    })
                  })
                )
                return styles;

              case "line":
                if (geometry.getType() === lineStringClassName){
                    styles.push(
                      new Style({
                        stroke: new Stroke(annotationsStyles.appStyleChart.candidateAnnotation.stroke)
                      })
                    )
                    geometry.forEachSegment(function (start, end) {
                      const dx = end[0] - start[0];
                      const dy = end[1] - start[1];
                      const rotation = Math.atan2(dy, dx);
                      // arrows
                      styles.push(
                        new Style({
                          geometry: new Point(end),
                          image: new Icon({
                            src: 'data/arrow.png',
                            anchor: [0.75, 0.5],
                            rotateWithView: true,
                            rotation: -rotation,
                          }),
                        })
                      );
                    });
                    return styles;
                  }
                break
              case "polygon":
                if (geometry.getType() === lineStringClassName){
                  styles.push(
                    new Style({
                      stroke: new Stroke(annotationsStyles.appStyleChart.candidateAnnotation.stroke)
                    })
                  )
                  geometry.forEachSegment(function (start, end) {
                    const dx = end[0] - start[0];
                    const dy = end[1] - start[1];
                    const rotation = Math.atan2(dy, dx);
                    // arrows
                    styles.push(
                      new Style({
                        geometry: new Point(end),
                        image: new Icon({
                          src: 'data/arrow.png',
                          anchor: [0.75, 0.5],
                          rotateWithView: true,
                          rotation: -rotation,
                        }),
                      })
                    );
                  });
                  return styles;
                }
                break
              default:
                // nothing
            }
          }
        };

        // drawn on the same layer, kept as two lists until here
        const annotationFeatures = annotations.map(annotation2feature);
        const textFeatures = textAnnotations.map(annotation2feature);
        const newFeatures = [...annotationFeatures, ...textFeatures];
        setFeatures(newFeatures)

        // create and add vector source layer
        const newFeaturesLayer = new VectorLayer({
          source: new VectorSource({
            features: [] // make sure features is an array
          }),
          style: featureStyleFunction({
            ...annotationsStyles.appStyleChart.feature,
            getColorRatio,
            selectedStyle: annotationsStyles.appStyleChart.selected,
            label: annotationsStyles.label,
            viewStyleChart: stateViewStyle,
            loadedImages,
          })
        })
        setFeaturesLayer(newFeaturesLayer)
        newFeaturesLayer.setSource(
          new VectorSource({
            features: newFeatures // make sure features is an array
          })
        )

        const source = new VectorSource();
        const newDrawingLayer = new VectorLayer({
          source: source,
          style: featureStyleFunction({
            ...annotationsStyles.appStyleChart.feature,
            getColorRatio: (() => undefined),
            selectedStyle: annotationsStyles.appStyleChart.selected,
            label: annotationsStyles.label,
            viewStyleChart: stateViewStyle,
          })
        })
        setDrawingLayer(newDrawingLayer)

        map.addLayer(newFeaturesLayer)
        map.addLayer(newDrawingLayer)

        // INTERACTIONS
        const interactions = initMapInteractions(
          map,
          dispatch,
          setNewGeometries,
          setSelectionBoxExtent,
          styleFunction,
          updateAnnotationState,
          setDisplayedPhoto,
          selectMultipleAnnotations,
          shiftKeyOnly,
          newFeaturesLayer,
          selectedFeaturesRef,
          setAnnotationsForGeometryEdit,
          annotationsRef,
          selectedAnnotationsRef,
          setTranslateOffsetDraft,
          setTextLabelDraft,
          textLabelDraftRef,
          selectTargetRef,
        );

        setInteractions(interactions);


        const handleKeyDown = (e) => {
          // Abort the drawing on Escape
          if (e.key === "Escape" && abortDrawingRef.current.annotationState === "DrawOnGoing") {
            // Retrieve the draw interaction
            let draw_interaction = null;
            if(abortDrawingRef.current.annotationMethod === "point") draw_interaction = interactions.drawPoint;
            else if (abortDrawingRef.current.annotationMethod === "polygon") draw_interaction = interactions.drawPolygon;
            else if (abortDrawingRef.current.annotationMethod === "square") draw_interaction = interactions.drawSquare;
            else if (abortDrawingRef.current.annotationMethod === "line") draw_interaction = interactions.drawLine;
            // If there is a current draw interaction
            if (draw_interaction != null){
              // Remove last point
              draw_interaction.removeLastPoint();
            }
          }
          // Finish drawing on Enter
          if (e.key === "Enter" && abortDrawingRef.current.annotationState === "DrawOnGoing") {
            // Retrieve the draw interaction
            let draw_interaction = null;
            if (abortDrawingRef.current.annotationMethod === "polygon") draw_interaction = interactions.drawPolygon;
            else if (abortDrawingRef.current.annotationMethod === "line") draw_interaction = interactions.drawLine;
            // If there is a current draw interaction
            if (draw_interaction != null){
              // Finish drawing
              draw_interaction.finishDrawing();
            }
          }
        };

        document.addEventListener("keydown", handleKeyDown);

        // change the binding pointermove
        unByKey(pointerMoveKey)
        setPointMoveKey(map.on('pointermove', pointerMoveAction(newView,viewInformation)))

        // change the binding resolution
        unByKey(changeResolutionKey)
        setChangeResolutionKey(newView.on('change:resolution', resolutionChangeAction(newView,viewInformation)))
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[stateLayers,authToken,map,viewInformation, loadedImages])

  // Update reference for the abort-drawing feature
  useEffect(() => {
    abortDrawingRef.current = {
      "annotationMethod": annotationMethod,
      "annotationState": annotationState,
      "annotationsForGeometryEdit": annotationsForGeometryEdit
    }
  }, [annotationMethod, annotationState, annotationsForGeometryEdit])

  // Keyboard shortcuts (annotate): set up map interactions
  // - s: selection (enable select, disable draw)
  // - e: edit
  // - m: move
  // - t: translate modal
  // - d: draw (New)
  useEffect(() => {
    // on toggle in any way of the select reset newGeometry potentially saved  during edit
    dispatch(setNewGeometries([]))
    // triggered once with a simple press on "s" key
    if (sKeyPressed === true && activePanel === "annotate") {
      // reset all state related to selection or edition on going 
      // here is the list of actions related to edition state that has to be cancelled properly:
      //  - SelectionOnGing state // means toggle on selection was made
        // 1. add selection interaction
        addSelectionInteraction();

        // 2. update annotation state
        dispatch(updateAnnotationState("SelectionOnGoing"))
        dispatch(selectMultipleAnnotations([]))
        dispatch(setAnnotationsForGeometryEdit([]))
    }
    // set to false again waiting for next "s" key press
    if (sKeyPressed === true) setSKeyPressed(false)
  }, [sKeyPressed])

  useEffect(() => {
    if (eKeyPressed === true && activePanel === "annotate") {
      //  list of possible states
      //  - EditOnGoing state =>
      //  - AnnotationRedrawOnGoing state =>  means Redraw button was triggered
      //  - DrawOnGoing state with annotationsForGeometryEdit.length > 0 => means we are drwing for an edition
      //  - AnnotationPostVertexEditOnGoing state =>
      //  - AnnotationPreVertexEditOnGoing state =>
      if ( annotationState === "AnnotationRedrawOnGoing") {
        // 1. add draw interaction
        addDrawInteraction();
        
        // 2. update states
        dispatch(selectMultipleAnnotations([]))
        dispatch(setAnnotationsForGeometryEdit([]))
        dispatch(updateAnnotationState("FormFilling"))

      } else if ((annotationsForGeometryEdit.length > 0 && annotationState === "DrawOnGoing")) {
        console.log('Geometry redraw is on going "e" key can"t be pressed unless you hit "esc" first')
      } else {
        // if there is selection
        if (selectedAnnotations.length === 1) {
          dispatch(updateAnnotationState("EditOnGoing"))
        } else {
          dispatch(updateAnnotationState("SelectionOnGoing"))
        }
      }
    }

    if (eKeyPressed === true) setEKeyPressed(false)
  }, [eKeyPressed])

  useEffect(() => {
    if (mKeyPressed === true && activePanel === "annotate") {
      // empty selection on move
      dispatch(selectMultipleAnnotations([]))
      dispatch(setAnnotationsForGeometryEdit([]))
      dispatch(updateAnnotationState("AnnotationsTranslationOnGoing"))
    }
    if (mKeyPressed === true) setMKeyPressed(false)
  }, [mKeyPressed])

  useEffect(() => {
    if (tKeyPressed === true && activePanel === "annotate" && selectedAnnotations.length > 0) {
      dispatch(updateAnnotationState("AnnotationsTranslateModalOnGoing"))
    }
    if (tKeyPressed === true) setTKeyPressed(false)
  }, [tKeyPressed])

  useEffect(() => {
    if (nKeyPressed === true && activePanel === "annotate") {
      dispatch(updateAnnotationState("FormFilling"));
      dispatch(selectMultipleAnnotations([]));
      dispatch(setAnnotationsForGeometryEdit([]));
    }
    if (nKeyPressed === true) setNKeyPressed(false);
  }, [nKeyPressed]);

  useEffect(() => {
    if (rKeyPressed === true && activePanel === "annotate") {
      dispatch(setAnnotationsForGeometryEdit([]))
      dispatch(updateAnnotationState("AnnotationsRemovePointsOnGoing"));
    }
    if (rKeyPressed === true) setRKeyPressed(false);
  }, [rKeyPressed]);

  // trigger by the l key in annotate
  // used to place a label, or to toggle Label when already in Select
  useEffect(() => {
    if (lKeyPressed === true && activePanel === "annotate") {
      dispatch(selectMultipleAnnotations([]));
      dispatch(setAnnotationsForGeometryEdit([]));
      if (annotationState === "SelectionOnGoing") {
        dispatch(setAnnotationSelectTarget(selectTarget === "text" ? "annotation" : "text"));
      } else {
        dispatch(updateAnnotationState("TextLabelOnGoing"));
      }
    }
    if (lKeyPressed === true) setLKeyPressed(false);
  }, [lKeyPressed]);

  // trigger by Ctrl+A or Command+A in Select
  // used to select every annotation, or every label when Label is on
  useEffect(() => {
    if (activePanel !== "annotate") return;
    const onKeyDown = (e) => {
      if (annotationState !== "SelectionOnGoing") return;
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== "a") return;
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target.isContentEditable) return;
      e.preventDefault();
      const source = selectTarget === "text" ? textAnnotations : annotations;
      dispatch(selectMultipleAnnotations(source.map((a) => a.id)));
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [activePanel, annotationState, selectTarget, annotations, textAnnotations, dispatch]);

  const keyPressedEvent = (e) => {
    if (e.key === "s" && e.target.tagName.toLowerCase() == 'body')
      setSKeyPressed(true);
    if (e.key === "e" && e.target.tagName.toLowerCase() == 'body')
      setEKeyPressed(true);
    if (e.key === "m" && e.target.tagName.toLowerCase() == 'body')
      setMKeyPressed(true);
    if (e.key === "t" && e.target.tagName.toLowerCase() == 'body')
      setTKeyPressed(true);
    if (e.key === "n" && e.target.tagName.toLowerCase() == 'body')
      setNKeyPressed(true);
    if (e.key === "r" && e.target.tagName.toLowerCase() == 'body')
      setRKeyPressed(true);
    if (e.key === "l" && e.target.tagName.toLowerCase() == 'body')
      setLKeyPressed(true);
  };

  // on Active panel change adjust key listeners
  useEffect(() => {
    if (activePanel === "annotate"){
      document.addEventListener("keyup", keyPressedEvent)
    } else {
      document.removeEventListener("keyup", keyPressedEvent)
    }
  }, [activePanel]);

  // handle setActive of one given interaction, deactivate all other interactions
  const setInteractionActive = (interactionName, isActive) => {
    const interaction = interactions[interactionName];
    if (interaction) {
      interaction.setActive(isActive);
    }
    
    setInteractions(prevInteractions => ({
      ...prevInteractions,
      [interactionName]: interaction,
    }));
  };

  // handle differents draw interactions active
  const addDrawInteraction = () => {
    const methodToInteractionName = {
      polygon: 'drawPolygon',
      line: 'drawLine',
      square: 'drawSquare',
      point: 'drawPoint'
    };
  
    const interactionName = methodToInteractionName[annotationMethod];
  
    // if interation not found
    if (!interactionName) {
      return;
    }
    // if interaction already active
    if (interactions[interactionName] && interactions[interactionName].getActive()) {
      return;
    }
    // reset all interactions
    Object.keys(interactions).forEach(name => setInteractionActive(name, false));
    // set interaction active
    setInteractionActive(interactionName, true);
  };
  
  

  const addSelectionInteraction = () => {
    // Deactivate all drawing and modifying interactions
    Object.keys(interactions).forEach(name => setInteractionActive(name, false));
  
    // Activate selection interactions
    setInteractionActive('selectSingleClick', true);
    setInteractionActive('selectionBox', true);
  };
  
  const addModifyInteraction = () => {
    Object.keys(interactions).forEach(name => setInteractionActive(name, false));
    
    if (annotationState === "AnnotationModifyOnGoing") {
      setInteractionActive("modify", true);
  
      if (selectedFeaturesRef.current.length === 1) {
        // passing selected feature this modify interaction
        interactions["modify"].features_.clear();
        interactions["modify"].features_.push(selectedFeaturesRef.current[0]);
      }
    }
  };
  

  const addPartialDrawPostVertexInteraction = () => {
    // Deactivate all drawing interactions
    Object.keys(interactions).forEach(name => setInteractionActive(name, false));
    setInteractionActive('partialDrawPostVertex', true);
  };

  const addPartialDrawPreVertexInteraction = () => {
    // Deactivate all drawing interactions
    Object.keys(interactions).forEach(name => setInteractionActive(name, false));
    setInteractionActive('partialDrawPreVertex', true);
  };

  const addTranslateInteraction = () => {
    // Deactivate all drawing interactions

    // TODO
    // (22/10/2025) => to handle multi-annotations translate, select interaction must be used  as the features sourc
    // and it must be kept active to access the selection
    // Object.keys(interactions).forEach((name) => {
    //   if (name != "selectSingleClick")
    //   setInteractionActive(name, false)
    // });


    Object.keys(interactions).forEach(name => setInteractionActive(name, false));
    setInteractionActive('translate', true);
  };

  const addTranslateByTwoClicksInteraction = () => {
    Object.keys(interactions).forEach(name => setInteractionActive(name, false));
    setInteractionActive('translateByTwoClicks', true);
  };

  const addRemovePointsInExtentInteraction = () => {
    Object.keys(interactions).forEach(name => setInteractionActive(name, false));
    setInteractionActive('removePointsInExtent', true);
  };

  useEffect(() => {
    if (annotationState === "AnnotationPostVertexEditOnGoing" || annotationState === "AnnotationPreVertexEditOnGoing") {
      if (!selectedFeaturesRef.current) return;
      if (annotationState === "AnnotationPostVertexEditOnGoing") {
        addPartialDrawPostVertexInteraction();
      } else if (annotationState === "AnnotationPreVertexEditOnGoing") {
        addPartialDrawPreVertexInteraction();
      }
    }
  }, [selectedFeaturesRefState]);

  // global useEffect handling:
  //  - map content changes
  //  - annotationState => change of state in annotations manipulations (edit/draw/create)
  //  - activePanel => toggle analyse/annotate
  //  - annotationMethod => toggle annotation draw type
  //  - TextLabelOnGoing => text label placement interaction
  useEffect(() => {
    if(map !== undefined){
      if (activePanel === "annotate"){
        if (annotationState === "TextLabelOnGoing") {
          Object.keys(interactions).forEach(name => setInteractionActive(name, false));
          setInteractionActive('textLabel', true);
        } else if (annotationState !== "SelectionOnGoing" &&
          annotationState !== "EditOnGoing" &&
          annotationState !== "AnnotationModifyOnGoing" &&
          annotationState !== "AnnotationPostVertexEditOnGoing" &&
          annotationState !== "AnnotationPreVertexEditOnGoing" &&
          annotationState !== "AnnotationsTranslationOnGoing" &&
          annotationState !== "AnnotationsTranslateByTwoClicksOnGoing" &&
          annotationState !== "AnnotationsTranslateModalOnGoing" &&
          annotationState !== "AnnotationsRemovePointsOnGoing") {
          // unselect annotations
          if (selectedAnnotations.length > 0) {
            dispatch(selectMultipleAnnotations([]))
            selectedFeaturesRef.current = [];
          }
          // toogle to draw
          addDrawInteraction();
        } else if (annotationState === "AnnotationModifyOnGoing") 
        {
          addModifyInteraction();
        } else if (
          annotationState === "AnnotationPostVertexEditOnGoing" ||
          annotationState === "AnnotationPreVertexEditOnGoing" )
        {
          // nothing for now
        } else if (annotationState === "AnnotationsTranslationOnGoing") {
          addTranslateInteraction();
        } else if (annotationState === "AnnotationsTranslateByTwoClicksOnGoing") {
          addTranslateByTwoClicksInteraction();
        } else if (annotationState === "AnnotationsTranslateModalOnGoing") {
          addSelectionInteraction();
        } else if (annotationState === "AnnotationsRemovePointsOnGoing") {
          addRemovePointsInExtentInteraction();
        } else {
          addSelectionInteraction();
        }
      }else{
        addSelectionInteraction();
        selectedFeaturesRef.current = [];

        // empty states for selection and annotation edition
        dispatch(setAnnotationsForGeometryEdit([]))

        // When going to the "Analyse" tab, drawing is aborted, and information about last annotation creation is removed
        dispatch(updateAnnotationState("SelectionOnGoing"))
      } 
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[activePanel,annotationMethod, map, annotationState])

  // Update OpenLayers selection when Redux selection changes
  useEffect(() => {
    if (featuresLayer == null) return;

    const source = featuresLayer.getSource();
    if (source == null) return;

    const selectionCollection = interactions.selectSingleClick.getFeatures();
    selectionCollection.clear();

    source.getFeatures().forEach((f) => {
      const annotationId = f.getProperties().annotation?.id;
      if (annotationId && selectedAnnotationsSet.has(annotationId)) {
        selectionCollection.push(f);
      }
    });
  }, [selectedAnnotationsSet, featuresLayer, interactions.selectSingleClick])



  /**
   * Handles moveToAnnotationId from Redux state.
   * This effect is triggered when AnnotationTablePage dispatches setMoveToAnnotationId.
   * When an annotation ID is set, the map view centers on that annotation.
   * 
   * The Redux state is synchronized across tabs/windows, so this works even if
   * AnnotationTablePage and MapWrapper are in different browser tabs.
   */
  const moveToAnnotationId = useSelector(selectMoveToAnnotationId);
  
  useEffect(() => {
    // Only proceed if we have all prerequisites
    if (!moveToAnnotationId || !viewInformation || !view || annotations.length === 0) {
      return;
    }

    // Find the annotation by ID
    const annotation = annotations.find(a => a.id === moveToAnnotationId);

    // Center the view on the annotation
    moveViewToAnnotation({view, a: annotation});
    // Reset the moveToAnnotationId after moving (prevents re-triggering)
    dispatch(setMoveToAnnotationId(null));
    
  }, [moveToAnnotationId, viewInformation, annotations, view, dispatch]);

  // trigger by the fit button on the scale
  // used to frame the map on the annotations
  useEffect(() => {
    if (!fitAnnotationsTick || !map) return;
    const features = featuresLayer?.getSource?.()?.getFeatures?.() || [];
    let extent = createEmpty();
    features.forEach((f) => {
      const g = f.getGeometry();
      if (g) extend(extent, g.getExtent());
    });
    if (isEmpty(extent) && viewInformation?.extent) {
      extent = viewInformation.extent;
    }
    if (isEmpty(extent) || !map.getSize()) return;

    // Same 25% margin as computeViewInformation
    const padX = Math.max((extent[2] - extent[0]) / 4, 1000);
    const padY = Math.max((extent[3] - extent[1]) / 4, 1000);
    extent = [extent[0] - padX, extent[1] - padY, extent[2] + padX, extent[3] + padY];

    map.getView().fit(extent, {
      ...fitMapOptions(map),
      duration: 250,
    });
  }, [fitAnnotationsTick, map, featuresLayer, viewInformation]);

  const handleTextLabelSubmit = ({ value, color }) => {
    setLabelColorDefault(color);
    setTextToCreate({ value, color });
  };

  return (
    <>
      <div ref={mapElement} className="map-container"></div>
      <TextLabelModal
        open={!!textLabelDraft}
        initialColor={labelColorDefault}
        onClose={() => dispatch(setTextLabelDraft(null))}
        onSubmit={handleTextLabelSubmit}
      />
      <CursorPosition cursorPositionInMap={cursorPositionInMap} />
      <Crackmeter currentResolutionInMap={currentResolutionInMap}/>
      <Scale
        currentResolutionInMap={currentResolutionInMap}
        onFitToAnnotations={() => setFitAnnotationsTick((n) => n + 1)}
      >
        <CursorPosition cursorPositionInMap={cursorPositionInMap} />
      </Scale>
      <PhotoPopupDisplayer />

      <TranslateModal map={map} featuresLayer={featuresLayer} onTranslateSavedRef={onTranslateSavedRef} />

      <ParallelProgressSnackbar
        open={geometrySaveSnack.open}
        running={geometrySaveSnack.running}
        severity={geometrySaveSnack.severity}
        message={geometrySaveSnack.message}
        onClose={() => setGeometrySaveSnack((s) => ({ ...s, open: false }))}
      />
    </>
  )

}

export default MapWrapper

